import { Component, EventEmitter, Input, OnDestroy, Output, Signal, ViewChild } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map, filter, take } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  DsButtonVariants,
  DsModalService,
  DsModalVariant,
  DsToggleComponent,
  DsToggleLabelPosition,
  Icons,
  IconStyles,
} from '@levelaccess/design-system';

import { IMfaStatusResponse } from '../../../../shared/interfaces/user.interface';
import { $mfaStatus } from '../../../../shared/constants/user';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { DsConfirmationModalComponent } from '../ds-confirmation-modal/ds-confirmation-modal.component';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { TranslateService } from '../../translate/translate.service';
import { FeatureFlagCollection } from '../../../../shared/interfaces/feature-flag.interface';
import { EapColors } from '../../shared/eap-colors';

@Component({
  selector: 'app-user-mfa',
  styleUrls: ['./user-mfa.component.scss'],
  templateUrl: './user-mfa.component.html',
  standalone: false,
})
export class UserMfaComponent implements OnDestroy {
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly DsToggleLabelPosition: typeof DsToggleLabelPosition = DsToggleLabelPosition;
  public readonly Icons: typeof Icons = Icons;
  public readonly IconStyles: typeof IconStyles = IconStyles;
  public readonly EapColors: typeof EapColors = EapColors;
  public $mfaStatus: typeof $mfaStatus;
  protected readonly userSettingsModalFF: Signal<boolean> = toSignal<boolean>(
    this.featureFlagService.variation$(FeatureFlagCollection.userSettingsModal, false).pipe(map(Boolean)),
  );
  @ViewChild(DsToggleComponent) private mfaToggleRef: DsToggleComponent | undefined;

  private subscriptions: Subscription;

  @Input()
  public mfaStatusResponse$: Observable<IMfaStatusResponse>;

  @Input()
  public disableMfaConfirmationMessage: string = 'mfa_disable_confirmation_message';

  @Output()
  public enableMfa: EventEmitter<void>;

  @Output()
  public disableMfa: EventEmitter<void>;

  constructor(
    private dsModalService: DsModalService,
    private featureFlagService: FeatureFlagService,
    private translateService: TranslateService,
  ) {
    this.subscriptions = new Subscription();

    this.$mfaStatus = $mfaStatus;
    this.enableMfa = new EventEmitter<void>();
    this.disableMfa = new EventEmitter<void>();
  }

  private closeDisableMfaModal(isConfirmedFn: () => boolean): void {
    if (!isConfirmedFn()) {
      return;
    }

    this.disableMfa.emit();
  }

  public get mfaIsConfigurable$(): Observable<boolean> {
    return this.mfaStatusResponse$.pipe(
      filter((response: IMfaStatusResponse): boolean => SharedCommonUtility.notNullish(response)),
      map((response: IMfaStatusResponse): boolean => response.isConfigurable),
    );
  }

  public get mfaStatus$(): Observable<$mfaStatus> {
    return this.mfaStatusResponse$.pipe(
      filter((response: IMfaStatusResponse): boolean => SharedCommonUtility.notNullish(response)),
      map((response: IMfaStatusResponse): $mfaStatus => response.isConfigurable && response.status),
    );
  }

  public get canEnableMfa(): boolean {
    return this.enableMfa.observers.length > 0;
  }

  public onMfaToggleChange(checked: boolean): void {
    this.mfaToggleRef?.writeValue(!checked);
    if (checked) {
      this.enableMfa.emit();
    } else {
      this.openDisableMfaModal();
    }
  }

  public openDisableMfaModal(): void {
    const modalRef = this.dsModalService.open(DsConfirmationModalComponent, DsModalVariant.center);
    const modalComponent: DsConfirmationModalComponent = modalRef.componentInstance as DsConfirmationModalComponent;
    modalComponent.title = this.translateService.instant('mfa_disable_confirmation_title');
    modalComponent.description = this.translateService.instant(this.disableMfaConfirmationMessage);
    modalComponent.confirmLabel = this.translateService.instant('label_ok');
    modalComponent.closeAllModals = false;

    this.subscriptions.add(modalComponent.confirm.pipe(take(1)).subscribe(() => this.closeDisableMfaModal(() => true)));
    this.subscriptions.add(modalComponent.cancelled.pipe(take(1)).subscribe(() => this.closeDisableMfaModal(() => false)));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
